import clsx from 'clsx';
import type { DeviceName } from '@athena-lib/device';
import { getResponsiveClasses, type Responsive, type Size } from '@athena-lib/responsive-utils';
import { objectKeys } from '@athena-lib/object-utils';
import type { ColumnWidth, ResponsiveColumnWidth } from './types';

const responsiveFlexBasis: Record<string, string> = {
  ['md_1/2']: 'md:basis-1/2',
  ['md_1/3']: 'md:basis-1/3',
  ['md_2/3']: 'md:basis-2/3',
  ['md_1/4']: 'md:basis-1/4',
  ['md_3/4']: 'md:basis-3/4',
  ['md_1/5']: 'md:basis-1/5',
  ['md_2/5']: 'md:basis-2/5',
  ['md_3/5']: 'md:basis-3/5',
  ['md_4/5']: 'md:basis-4/5',

  ['lg_1/2']: 'lg:basis-1/2',
  ['lg_1/3']: 'lg:basis-1/3',
  ['lg_2/3']: 'lg:basis-2/3',
  ['lg_1/4']: 'lg:basis-1/4',
  ['lg_3/4']: 'lg:basis-3/4',
  ['lg_1/5']: 'lg:basis-1/5',
  ['lg_2/5']: 'lg:basis-2/5',
  ['lg_3/5']: 'lg:basis-3/5',
  ['lg_4/5']: 'lg:basis-4/5',
};

const flexBasisClasses: Record<ColumnWidth, string> = {
  '1/2': 'basis-1/2',
  '1/3': 'basis-1/3',
  '2/3': 'basis-2/3',
  '1/4': 'basis-1/4',
  '3/4': 'basis-3/4',
  '1/5': 'basis-1/5',
  '2/5': 'basis-2/5',
  '3/5': 'basis-3/5',
  '4/5': 'basis-4/5',
  auto: '',
  content: '',
};

export const getFlexBasisClass = (width: ColumnWidth | ResponsiveColumnWidth | undefined) => {
  if (!width || width === 'content') {
    return '';
  }

  if (typeof width === 'string') {
    return flexBasisClasses[width];
  }

  if (typeof width === 'object') {
    const classes: string[] = [];
    for (const deviceName of objectKeys(width)) {
      classes.push(responsiveFlexBasis[deviceName + '_' + width[deviceName]]);
    }
    return clsx(classes);
  }

  return 'basis-full';
};

const collapseBelowClasses: Record<DeviceName, string> = {
  sm: 'sm:flex-row',
  md: 'md:flex-row',
  lg: 'lg:flex-row',
};

export const getFlexClasses = (
  collapseBelow: DeviceName | undefined,
  collapse: boolean | undefined,
  reverseWhenCollapsed: boolean | undefined,
  reverse: boolean | Responsive<boolean> | undefined,
) => {
  if (collapse) {
    return 'flex flex-col';
  }
  if (!collapseBelow) {
    return 'flex';
  }
  const classes = ['flex'];
  if (reverseWhenCollapsed) {
    classes.push('flex-col-reverse');
  } else {
    classes.push('flex-col');
  }
  if (collapseBelow) {
    classes.push(collapseBelowClasses[collapseBelow]);
  }
  if (reverse !== undefined) {
    classes.push(...getReverseClasses(reverse));
  }
  return classes;
};

const getReverseClasses = (reverse: boolean | Responsive<boolean>) => {
  const classes: string[] = [];
  const reverseClasses: Record<DeviceName, [string, string]> = {
    sm: ['flex-row-reverse', 'flex-row'],
    md: ['md:flex-row-reverse', 'md:flex-row'],
    lg: ['lg:flex-row-reverse', 'lg:flex-row'],
  };

  if (typeof reverse === 'object') {
    for (const deviceName of objectKeys(reverse)) {
      if (reverse[deviceName]) {
        classes.push(reverseClasses[deviceName][0]);
      } else {
        classes.push(reverseClasses[deviceName][1]);
      }
    }
  } else if (reverse) {
    classes.push('flex-row-reverse');
  }
  return classes;
};

export const getLeftSpaceClasses = (
  size: Size | 'none' | undefined,
  collapseBelow: DeviceName | undefined,
) => {
  if (collapseBelow) {
    return getResponsiveClasses({ [collapseBelow]: size }, leftSpaceClasses);
  }
  return getResponsiveClasses(size, leftSpaceClasses);
};

export const getRightSpaceClasses = (
  size: Size | 'none' | undefined,
  collapseBelow: DeviceName | undefined,
) => {
  if (collapseBelow) {
    return getResponsiveClasses({ [collapseBelow]: size }, rightSpaceClasses);
  }
  return getResponsiveClasses(size, rightSpaceClasses);
};

const leftSpaceClasses = {
  default: {
    none: '',
    small: 'pl-small',
    medium: 'pl-gutter',
    large: 'pl-large',
    xlarge: 'pl-xlarge',
    xxlarge: 'pl-xxlarge',
  },

  md: {
    none: '',
    small: 'md:pl-small',
    medium: 'md:pl-gutter',
    large: 'md:pl-large',
    xlarge: 'md:pl-xlarge',
    xxlarge: 'md:pl-xxlarge',
  },

  lg: {
    none: '',
    small: 'lg:pl-small',
    medium: 'lg:pl-gutter',
    large: 'lg:pl-large',
    xlarge: 'lg:pl-xlarge',
    xxlarge: 'lg:pl-xxlarge',
  },
};

const rightSpaceClasses = {
  default: {
    none: '',
    small: 'pr-small',
    medium: 'pr-gutter',
    large: 'pr-large',
    xlarge: 'pr-xlarge',
    xxlarge: 'pr-xxlarge',
  },

  md: {
    none: '',
    small: 'md:pr-small',
    medium: 'md:pr-gutter',
    large: 'md:pr-large',
    xlarge: 'md:pr-xlarge',
    xxlarge: 'md:pr-xxlarge',
  },

  lg: {
    none: '',
    small: 'lg:pr-small',
    medium: 'lg:pr-gutter',
    large: 'lg:pr-large',
    xlarge: 'lg:pr-xlarge',
    xxlarge: 'lg:pr-xxlarge',
  },
};
